import { useSessionStore } from '@/entities/session';
import { useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, type RefObject } from 'react';
import {
  enqueueTrackingEvent,
  flushTrackingQueue,
  getTrackingSessionId,
  startTrackingFlushLoop,
  stopTrackingFlushLoop,
} from './tracking-client';

const HEARTBEAT_INTERVAL_MS = 30_000;
// Time only counts as "active" while the tab is visible and the user has used the
// mouse, keyboard, touch or scroll within the last IDLE_AFTER_MS.
const ACTIVE_TICK_MS = 5_000;
const IDLE_AFTER_MS = 120_000;
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'scroll',
  'touchstart',
];
const MAX_LABEL_LENGTH = 60;

const clampScrollDepth = (value: number) =>
  Math.round(Math.min(100, Math.max(0, value)));

const getElementLabel = (target: EventTarget | null): string | undefined => {
  if (!(target instanceof Element)) return undefined;

  const testIdHost = target.closest('[data-testid]');
  if (testIdHost) {
    return testIdHost.getAttribute('data-testid') ?? undefined;
  }

  const ariaHost = target.closest('[aria-label]');
  if (ariaHost) {
    return ariaHost.getAttribute('aria-label') ?? undefined;
  }

  const rawText =
    target instanceof HTMLElement ? target.innerText : target.textContent;
  const text = rawText?.trim();
  if (text) {
    return text.length > MAX_LABEL_LENGTH
      ? `${text.slice(0, MAX_LABEL_LENGTH)}…`
      : text;
  }

  return target.tagName.toLowerCase();
};

/**
 * Mounts the whole client-side tracking SDK: heartbeats, page enter/exit +
 * scroll depth on route change, and delegated click capture. Intended to be
 * called once, near the root of the authenticated app shell (see
 * `PlatformLayout`) — mounting it there, rather than globally, is what keeps
 * `/login` and other unauthenticated routes untracked, since that layout only
 * renders once `isAuthenticated` is true.
 *
 * `scrollContainerRef` should point at whichever element actually scrolls.
 * The platform layout scrolls an inner `overflow-auto` div, not the window,
 * so pass that ref in; omit it only where `window`/`document` is the
 * scrolling element.
 */
export const usePageTracking = (
  scrollContainerRef?: RefObject<HTMLElement | null>
) => {
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated);
  // Organization records surfaced to the client mark demo orgs with `isDemo`
  // (see `entities/organization`); there is no separate client-side
  // "impersonating" flag today (impersonation is handled by exchanging for a
  // new session token, not a flag on the current session) — real, non-demo
  // impersonated sessions are still tracked.
  const isDemoOrg = useSessionStore(
    (s) => s.user?.organization?.isDemo ?? false
  );
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const trackingEnabled = Boolean(isAuthenticated) && !isDemoOrg;

  const currentPageViewIdRef = useRef<string | null>(null);
  const currentPathRef = useRef<string | null>(null);
  const maxScrollDepthRef = useRef(0);
  const rafPendingRef = useRef(false);
  const lastActivityAtRef = useRef(Date.now());
  const lastTickAtRef = useRef(Date.now());
  const activeMsRef = useRef(0);

  // Adds the time since the last call to the active clock if the user is active now.
  const accrueActiveTime = useCallback(() => {
    const now = Date.now();
    const elapsed = now - lastTickAtRef.current;
    lastTickAtRef.current = now;

    if (
      document.visibilityState === 'visible' &&
      now - lastActivityAtRef.current < IDLE_AFTER_MS
    ) {
      activeMsRef.current += Math.min(elapsed, ACTIVE_TICK_MS * 2);
    }
  }, []);

  // Whole seconds of active use since the last call (the remainder carries over).
  const takeActiveSeconds = useCallback(() => {
    accrueActiveTime();
    const seconds = Math.floor(activeMsRef.current / 1000);
    activeMsRef.current -= seconds * 1000;
    return seconds;
  }, [accrueActiveTime]);

  const enqueueHeartbeat = useCallback(() => {
    enqueueTrackingEvent({
      type: 'session_heartbeat',
      sessionId: getTrackingSessionId(),
      pageViewId: currentPageViewIdRef.current ?? undefined,
      occurredAt: new Date().toISOString(),
      activeSeconds: takeActiveSeconds(),
    });
  }, [takeActiveSeconds]);

  // Active-time clock: input listeners + a steady tick.
  useEffect(() => {
    if (!trackingEnabled) return;

    const markActivity = () => {
      lastActivityAtRef.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, markActivity, {
        passive: true,
        capture: true,
      })
    );
    lastTickAtRef.current = Date.now();
    const tick = setInterval(accrueActiveTime, ACTIVE_TICK_MS);

    return () => {
      clearInterval(tick);
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, markActivity, { capture: true })
      );
    };
  }, [trackingEnabled, accrueActiveTime]);

  // Batch flush loop + flush-on-hide/unload, for the lifetime of tracking
  // being enabled.
  useEffect(() => {
    if (!trackingEnabled) return;

    startTrackingFlushLoop();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        enqueueHeartbeat();
        flushTrackingQueue(true);
      }
    };
    const handleBeforeUnload = () => {
      enqueueHeartbeat();
      flushTrackingQueue(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      stopTrackingFlushLoop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [trackingEnabled, enqueueHeartbeat]);

  // Heartbeat every 30s, paused while the tab is hidden.
  useEffect(() => {
    if (!trackingEnabled) return;

    let interval: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (interval) return;
      enqueueHeartbeat();
      interval = setInterval(enqueueHeartbeat, HEARTBEAT_INTERVAL_MS);
    };
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    if (document.visibilityState === 'visible') start();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [trackingEnabled, enqueueHeartbeat]);

  // Max scroll depth on the current page, rAF-throttled.
  useEffect(() => {
    if (!trackingEnabled) return;

    const container = scrollContainerRef?.current ?? null;
    const target: HTMLElement | Window = container ?? window;

    const readScrollDepth = () => {
      rafPendingRef.current = false;

      const { scrollTop, scrollHeight, clientHeight } = container
        ? container
        : {
            scrollTop: window.scrollY,
            scrollHeight: document.documentElement.scrollHeight,
            clientHeight: window.innerHeight,
          };

      if (scrollHeight <= 0) return;

      const depth = clampScrollDepth(
        ((scrollTop + clientHeight) / scrollHeight) * 100
      );

      if (depth > maxScrollDepthRef.current) {
        maxScrollDepthRef.current = depth;
      }
    };

    const handleScroll = () => {
      if (rafPendingRef.current) return;
      rafPendingRef.current = true;
      requestAnimationFrame(readScrollDepth);
    };

    readScrollDepth();
    target.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      target.removeEventListener('scroll', handleScroll);
    };
    // Re-attach whenever the page changes too, so a container swap (or a
    // fresh scroll height on the new page) is picked up immediately.
  }, [trackingEnabled, scrollContainerRef, pathname]);

  // Delegated click capture.
  useEffect(() => {
    if (!trackingEnabled) return;

    const handleClick = (event: MouseEvent) => {
      enqueueTrackingEvent({
        type: 'click',
        sessionId: getTrackingSessionId(),
        pageViewId: currentPageViewIdRef.current ?? undefined,
        occurredAt: new Date().toISOString(),
        path: currentPathRef.current ?? undefined,
        elementLabel: getElementLabel(event.target),
      });
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [trackingEnabled]);

  // Page enter/exit on route change.
  useEffect(() => {
    if (!trackingEnabled) return;

    const previousPageViewId = currentPageViewIdRef.current;
    const previousPath = currentPathRef.current;
    const previousMaxScrollDepth = maxScrollDepthRef.current;

    if (previousPageViewId && previousPath) {
      enqueueTrackingEvent({
        type: 'page_exit',
        sessionId: getTrackingSessionId(),
        pageViewId: previousPageViewId,
        occurredAt: new Date().toISOString(),
        path: previousPath,
        scrollDepthPercent: clampScrollDepth(previousMaxScrollDepth),
        activeSeconds: takeActiveSeconds(),
      });
    }

    const newPageViewId = crypto.randomUUID();
    currentPageViewIdRef.current = newPageViewId;
    currentPathRef.current = pathname;
    maxScrollDepthRef.current = 0;

    enqueueTrackingEvent({
      type: 'page_enter',
      sessionId: getTrackingSessionId(),
      pageViewId: newPageViewId,
      occurredAt: new Date().toISOString(),
      path: pathname,
    });

    // Navigations are a likely place for a tab to close shortly after, so
    // flush the outgoing page_exit right away instead of waiting for the
    // 10s/20-event batch.
    flushTrackingQueue(false);
  }, [trackingEnabled, pathname, takeActiveSeconds]);

  // Close out the last page view if tracking stops without a route change
  // (e.g. logout).
  useEffect(() => {
    return () => {
      if (!trackingEnabled) return;

      const pageViewId = currentPageViewIdRef.current;
      const path = currentPathRef.current;
      if (!pageViewId || !path) return;

      enqueueTrackingEvent({
        type: 'page_exit',
        sessionId: getTrackingSessionId(),
        pageViewId,
        occurredAt: new Date().toISOString(),
        path,
        scrollDepthPercent: clampScrollDepth(maxScrollDepthRef.current),
        activeSeconds: takeActiveSeconds(),
      });
      flushTrackingQueue(true);
    };
  }, [trackingEnabled, takeActiveSeconds]);
};
